<template>
  <!-- Curated brand picker for the "Track a Brand in Spyder" guided flow.
  Brands live in utils/setup/curatedBrands.js; all of them have rich Spyder
  data, so the walkthrough that follows always has something to show. -->
  <BaseModal
    card-width="640px"
    no-padding
    no-toolbar
    no-exit
    @close="$emit('close')"
  >
    <div class="flex flex-col">
      <div class="flex flex-col px-6 pt-4">
        <!-- Header: kicker + close -->
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-1.5 text-text-subdued">
            <MouseIcon
              :width="14"
              :height="14"
            />
            <BaseText
              type="heading"
              size="overline"
              class="uppercase"
            >
              {{ copy.kicker }}
            </BaseText>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="-mr-1.5 p-1 rounded-md text-icon-normal transition-colors duration-150 hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
            @click="$emit('close')"
          >
            <TagRemoveIcon
              :width="16"
              :height="16"
            />
          </button>
        </div>

        <BaseText
          type="display"
          size="xs"
          class="text-text-loud mt-3"
        >
          {{ copy.title }}
        </BaseText>
        <BaseText
          type="body"
          size="sm"
          class="text-text-muted mt-1.5"
        >
          {{ copy.blurb }}
        </BaseText>

        <SetupBrandCategoryTabs
          class="mt-5"
          :categories="categories"
          :value="selectedCategoryId"
          @input="selectCategory"
        />

        <!-- Sub-category chips (eCommerce) -->
        <div
          v-if="subcategories.length"
          class="flex items-center flex-wrap gap-1.5 mt-2.5"
        >
          <button
            v-for="subcategory in subcategories"
            :key="subcategory.id"
            type="button"
            class="flex-shrink-0 px-2.5 py-1 rounded-full border transition-colors duration-150 focus:outline-none"
            :class="selectedSubcategoryId === subcategory.id
              ? 'border-neutral-100 bg-neutral-50 text-text-loud'
              : 'border-border-normal bg-white text-text-subdued hover:text-text-muted hover:border-border-hover'"
            @click="selectSubcategory(subcategory.id)"
          >
            <BaseText
              type="label"
              size="xs"
            >
              {{ subcategory.label }}
            </BaseText>
          </button>
        </div>

        <!-- "Top 12 in …" so the list doesn't read as the whole catalogue -->
        <div class="flex items-center gap-2 mt-4 mb-2">
          <BaseText
            type="heading"
            size="overline"
            class="uppercase text-text-subdued"
          >
            {{ gridCaption }}
          </BaseText>
          <span class="w-1 h-1 rounded-full bg-neutral-200" />
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued"
          >
            {{ copy.gridCaptionMore }}
          </BaseText>
        </div>
      </div>

      <!-- Brand grid -->
      <div class="grid grid-cols-2 gap-2 px-6 pb-1">
        <SetupBrandCard
          v-for="brand in visibleBrands"
          :key="brand.pageId"
          :brand="brand"
          :selected="selectedBrand?.pageId === brand.pageId"
          @select="selectedBrand = $event"
        />
      </div>

      <!-- The full search lives in Spyder; plain text so nobody leaves this flow to get there -->
      <div class="flex items-start gap-2 px-6 pt-3 pb-4">
        <SearchIcon
          :width="14"
          :height="14"
          class-name="text-icon-disabled flex-shrink-0 mt-px"
        />
        <BaseText
          type="body"
          size="xs"
          class="text-text-subdued"
        >
          {{ copy.moreBrandsNote }}
        </BaseText>
      </div>

      <!-- Footer: credits (same pill as SpyderBrandModal) + confirm -->
      <div class="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-border-normal">
        <div class="flex items-center gap-1.5 rounded-full border border-border-normal pl-0.5 py-0.5 pr-3">
          <img
            src="../../assets/icons/spyder-token.png"
            class="w-7 h-7"
          >
          <BaseText
            type="label"
            size="sm"
            :class="outOfCredits ? 'text-secondary-red-200' : 'text-text-muted'"
          >
            {{ getSpyderSubscriptions.length }}/{{ getSpyderLimit }} credits{{ outOfCredits ? ` — ${copy.outOfCreditsNote}` : '' }}
          </BaseText>
        </div>
        <BaseButton
          primary
          forced-theme="red"
          class="w-40"
          :disabled="!selectedBrand || tracking || outOfCredits"
          :loading="tracking"
          @click="trackSelected"
        >
          {{ tracking ? copy.ctaLabelLoading : copy.ctaLabel }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import posthog from 'posthog-js'
import ForeplayAPI from '@/api/foreplayServer'
import { CURATED_BRAND_CATEGORIES, MAX_TOP_LEVEL_BRANDS, MAX_SUBCATEGORY_BRANDS } from '@/utils/setup/curatedBrands'
import { SETUP_BRAND_PICKER } from '@/utils/setup/setupContent'
import SetupBrandCategoryTabs from './SetupBrandCategoryTabs.vue'
import SetupBrandCard from './SetupBrandCard.vue'
import MouseIcon from '../globals/Icons/MouseIcon.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'

export default {
  name: 'SetupBrandPickerModal',
  components: {
    SetupBrandCategoryTabs,
    SetupBrandCard,
    MouseIcon,
    SearchIcon,
    TagRemoveIcon
  },
  data () {
    return {
      copy: SETUP_BRAND_PICKER,
      categories: CURATED_BRAND_CATEGORIES,
      selectedCategoryId: CURATED_BRAND_CATEGORIES[0].id,
      selectedSubcategoryId: null,
      selectedBrand: null,
      tracking: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getSpyderLimit']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions']),
    selectedCategory () {
      return this.categories.find(category => category.id === this.selectedCategoryId)
    },
    subcategories () {
      return this.selectedCategory.subcategories || []
    },
    selectedSubcategory () {
      return this.subcategories.find(entry => entry.id === this.selectedSubcategoryId) || this.subcategories[0] || null
    },
    visibleBrands () {
      if (this.selectedSubcategory) return this.selectedSubcategory.brands.slice(0, MAX_SUBCATEGORY_BRANDS)
      return this.selectedCategory.brands.slice(0, MAX_TOP_LEVEL_BRANDS)
    },
    // "Top 12 in Foreplay" for the popular tab, otherwise the (sub)category name
    gridCaption () {
      const label = this.selectedCategoryId === 'popular'
        ? 'Foreplay'
        : (this.selectedSubcategory || this.selectedCategory).label
      return this.copy.gridCaption
        .replace('{count}', this.visibleBrands.length)
        .replace('{category}', label)
    },
    alreadyTracked () {
      if (!this.selectedBrand) return false
      return this.getSpyderSubscriptions.some(entry =>
        entry.subscription?.pageId === this.selectedBrand.pageId || entry.id === this.selectedBrand.brandId
      )
    },
    // Re-tracking an already-tracked brand costs nothing, so only block new adds
    outOfCredits () {
      return !this.alreadyTracked && this.getSpyderSubscriptions.length >= this.getSpyderLimit
    }
  },
  created () {
    this.fetchSpyderSubscriptions({ ignoreDexie: true })
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    selectCategory (categoryId) {
      this.selectedCategoryId = categoryId
      this.selectedSubcategoryId = this.subcategories.length ? this.subcategories[0].id : null
      this.selectedBrand = null
    },
    selectSubcategory (subcategoryId) {
      this.selectedSubcategoryId = subcategoryId
      this.selectedBrand = null
    },
    async trackSelected () {
      const brand = this.selectedBrand
      if (!brand || this.tracking) return

      this.tracking = true
      try {
        if (!this.alreadyTracked) {
          const { data } = await ForeplayAPI.Spyder.addTrackedBrands([{ pageId: brand.pageId }])
          if (data.some(addedBrand => addedBrand?.status === 'error')) {
            this.$showAlert({ type: 'error', message: 'Could not track that brand — try another one.' })
            return
          }
          await this.fetchSpyderSubscriptions({ ignoreDexie: true })
        }
        posthog.capture('Setup Brand Picker Tracked', {
          brand_name: brand.name,
          brand_id: brand.brandId,
          category: this.selectedCategoryId,
          subcategory: this.selectedSubcategoryId,
          already_tracked: this.alreadyTracked
        })
        this.$emit('tracked', brand)
      } catch (error) {
        console.error('Error tracking brand', error)
        this.$showAlert({ type: 'error', message: 'Could not track that brand — try another one.' })
      } finally {
        this.tracking = false
      }
    }
  }
}
</script>
